import { supabase, getSchema } from '@/lib/supabase';
import type {
  ProjectPhoto,
  ProjectPhotoView,
  UnassignedPhotoView,
  ProjectMatch,
  UploadPhotoParams,
} from '../types';

/**
 * Get the storage bucket name based on environment
 */
function getPhotoBucketName(): string {
  const schema = getSchema();
  return schema === 'testing' ? 'project-photos-testing' : 'project-photos';
}

/**
 * Build the storage path for a photo
 */
function buildStoragePath(
  businessId: string,
  photoId: string,
  fileName: string,
  projectId?: string | null
): string {
  const extension = fileName.split('.').pop()?.toLowerCase() || 'jpg';
  const folder = projectId || 'unassigned';
  return `${businessId}/${folder}/${photoId}.${extension}`;
}

/**
 * Find nearest project sites to given coordinates
 */
export async function findNearestProjectSite(
  businessId: string,
  lat: number,
  lng: number,
  maxDistanceM: number = 500
): Promise<ProjectMatch[]> {
  const { data, error } = await supabase.rpc('find_nearest_project_site', {
    p_business_id: businessId,
    p_lat: lat,
    p_lng: lng,
    p_max_distance_m: maxDistanceM,
    p_project_status: ['planned', 'in_progress'],
  });

  if (error) {
    console.error('Error finding nearest project site:', error);
    throw error;
  }

  return (data || []).map((row: any) => ({
    projectId: row.project_id,
    projectName: row.project_name,
    siteId: row.site_id,
    siteAddress: row.site_address,
    distanceM: row.distance_m,
    confidence: row.confidence as 'high' | 'medium' | 'low' | 'none',
  }));
}

/**
 * Upload a photo to storage and create database record
 */
export async function uploadPhoto(params: UploadPhotoParams): Promise<ProjectPhoto> {
  const {
    file,
    businessId,
    uploadedBy,
    projectId,
    exif,
    suggestedProjectId,
    matchDistanceM,
    matchConfidence,
    description,
  } = params;

  const bucketName = getPhotoBucketName();
  const photoId = crypto.randomUUID();
  const filePath = buildStoragePath(businessId, photoId, file.name, projectId);

  // 1. Upload file to storage
  const { error: uploadError } = await supabase.storage
    .from(bucketName)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    console.error('Error uploading file:', uploadError);
    throw uploadError;
  }

  // 2. Create database record
  const assignmentStatus = projectId ? 'assigned' : 'pending_assignment';

  const photoRecord = {
    id: photoId,
    business_id: businessId,
    project_id: projectId || null,
    uploaded_by: uploadedBy,
    file_name: file.name,
    file_path: filePath,
    file_size_bytes: file.size,
    mime_type: file.type,
    taken_at: exif.takenAt?.toISOString() || null,
    camera_make: exif.cameraMake,
    camera_model: exif.cameraModel,
    lat: exif.lat,
    lng: exif.lng,
    gps_accuracy: exif.gpsAccuracy,
    suggested_project_id: suggestedProjectId || null,
    match_distance_m: matchDistanceM || null,
    match_confidence: matchConfidence || null,
    assignment_status: assignmentStatus,
    assigned_at: projectId ? new Date().toISOString() : null,
    description: description || null,
  };

  const { data, error: insertError } = await supabase
    .from('project_photos')
    .insert(photoRecord)
    .select()
    .single();

  if (insertError) {
    // Try to clean up uploaded file
    await supabase.storage.from(bucketName).remove([filePath]);
    console.error('Error creating photo record:', insertError);
    throw insertError;
  }

  return mapPhotoFromDb(data);
}

/**
 * Assign a photo to a project
 */
export async function assignPhotoToProject(
  photoId: string,
  projectId: string,
  businessId: string
): Promise<ProjectPhoto> {
  const bucketName = getPhotoBucketName();

  // Get current photo record
  const { data: currentPhoto, error: fetchError } = await supabase
    .from('project_photos')
    .select('*')
    .eq('id', photoId)
    .single();

  if (fetchError || !currentPhoto) {
    throw fetchError || new Error('Photo not found');
  }

  const oldPath = currentPhoto.file_path;
  const extension = oldPath.split('.').pop();
  const newPath = `${businessId}/${projectId}/${photoId}.${extension}`;

  // Move file in storage if path is changing
  if (oldPath !== newPath) {
    const { error: moveError } = await supabase.storage
      .from(bucketName)
      .move(oldPath, newPath);

    if (moveError) {
      console.error('Error moving file:', moveError);
      throw moveError;
    }
  }

  // Update database record
  const { data, error: updateError } = await supabase
    .from('project_photos')
    .update({
      project_id: projectId,
      file_path: newPath,
      assignment_status: 'assigned',
      assigned_at: new Date().toISOString(),
    })
    .eq('id', photoId)
    .select()
    .single();

  if (updateError) {
    // Try to move file back
    if (oldPath !== newPath) {
      await supabase.storage.from(bucketName).move(newPath, oldPath);
    }
    throw updateError;
  }

  return mapPhotoFromDb(data);
}

/**
 * Mark a photo as unassigned (no project)
 */
export async function markPhotoUnassigned(photoId: string): Promise<ProjectPhoto> {
  const { data, error } = await supabase
    .from('project_photos')
    .update({
      assignment_status: 'unassigned',
    })
    .eq('id', photoId)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return mapPhotoFromDb(data);
}

/**
 * Get photos for a project
 */
export async function getProjectPhotos(projectId: string): Promise<ProjectPhotoView[]> {
  const { data, error } = await supabase
    .from('v_project_photos')
    .select('*')
    .eq('project_id', projectId)
    .order('taken_at', { ascending: false, nullsFirst: false });

  if (error) {
    throw error;
  }

  return (data || []).map(mapPhotoViewFromDb);
}

/**
 * Get unassigned photos for the business
 */
export async function getUnassignedPhotos(businessId: string): Promise<UnassignedPhotoView[]> {
  const { data, error } = await supabase
    .from('v_unassigned_photos')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return (data || []).map(mapUnassignedPhotoFromDb);
}

/**
 * Get a signed URL for viewing a photo
 */
export async function getPhotoUrl(filePath: string, expiresIn: number = 3600): Promise<string> {
  const bucketName = getPhotoBucketName();

  const { data, error } = await supabase.storage
    .from(bucketName)
    .createSignedUrl(filePath, expiresIn);

  if (error) {
    throw error;
  }

  return data.signedUrl;
}

/**
 * Unassign multiple photos from their project (batch operation)
 */
export async function unassignPhotosFromProject(
  photoIds: string[],
  businessId: string
): Promise<{ success: string[]; failed: string[] }> {
  const bucketName = getPhotoBucketName();
  const success: string[] = [];
  const failed: string[] = [];

  for (const photoId of photoIds) {
    try {
      // Get current photo record
      const { data: currentPhoto, error: fetchError } = await supabase
        .from('project_photos')
        .select('*')
        .eq('id', photoId)
        .single();

      if (fetchError || !currentPhoto) {
        failed.push(photoId);
        continue;
      }

      const oldPath = currentPhoto.file_path;
      const extension = oldPath.split('.').pop();
      const newPath = `${businessId}/unassigned/${photoId}.${extension}`;

      // Move file in storage if path is changing
      if (oldPath !== newPath) {
        const { error: moveError } = await supabase.storage
          .from(bucketName)
          .move(oldPath, newPath);

        if (moveError) {
          console.error(`Error moving file for photo ${photoId}:`, moveError);
          failed.push(photoId);
          continue;
        }
      }

      // Update database record
      const { error: updateError } = await supabase
        .from('project_photos')
        .update({
          project_id: null,
          file_path: newPath,
          assignment_status: 'pending_assignment',
          assigned_at: null,
        })
        .eq('id', photoId);

      if (updateError) {
        // Try to move file back
        if (oldPath !== newPath) {
          await supabase.storage.from(bucketName).move(newPath, oldPath);
        }
        failed.push(photoId);
        continue;
      }

      success.push(photoId);
    } catch (error) {
      console.error(`Error unassigning photo ${photoId}:`, error);
      failed.push(photoId);
    }
  }

  return { success, failed };
}

/**
 * Delete a photo
 */
export async function deletePhoto(photoId: string, filePath: string): Promise<void> {
  const bucketName = getPhotoBucketName();

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(bucketName)
    .remove([filePath]);

  if (storageError) {
    console.error('Error deleting file from storage:', storageError);
  }

  // Delete from database
  const { error: dbError } = await supabase
    .from('project_photos')
    .delete()
    .eq('id', photoId);

  if (dbError) {
    throw dbError;
  }
}

/**
 * Update photo description
 */
export async function updatePhotoDescription(
  photoId: string,
  description: string
): Promise<ProjectPhoto> {
  const { data, error } = await supabase
    .from('project_photos')
    .update({
      description: description || null,
    })
    .eq('id', photoId)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return mapPhotoFromDb(data);
}

// Mapping functions
function mapPhotoFromDb(row: any): ProjectPhoto {
  return {
    id: row.id,
    businessId: row.business_id,
    projectId: row.project_id,
    uploadedBy: row.uploaded_by,
    fileName: row.file_name,
    filePath: row.file_path,
    fileSizeBytes: row.file_size_bytes,
    mimeType: row.mime_type,
    takenAt: row.taken_at,
    cameraMake: row.camera_make,
    cameraModel: row.camera_model,
    lat: row.lat,
    lng: row.lng,
    gpsAccuracy: row.gps_accuracy,
    suggestedProjectId: row.suggested_project_id,
    matchDistanceM: row.match_distance_m,
    matchConfidence: row.match_confidence,
    assignmentStatus: row.assignment_status,
    assignedAt: row.assigned_at,
    description: row.description,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapPhotoViewFromDb(row: any): ProjectPhotoView {
  return {
    ...mapPhotoFromDb(row),
    hasCoordinates: row.has_coordinates,
    projectName: row.project_name,
    projectStatus: row.project_status,
    customerFirstName: row.customer_first_name,
    customerLastName: row.customer_last_name,
    customerName: row.customer_name,
    uploadedByName: row.uploaded_by_name,
    suggestedProjectName: row.suggested_project_name,
  };
}

function mapUnassignedPhotoFromDb(row: any): UnassignedPhotoView {
  return {
    id: row.id,
    businessId: row.business_id,
    uploadedBy: row.uploaded_by,
    fileName: row.file_name,
    filePath: row.file_path,
    takenAt: row.taken_at,
    lat: row.lat,
    lng: row.lng,
    hasCoordinates: row.has_coordinates,
    suggestedProjectId: row.suggested_project_id,
    matchDistanceM: row.match_distance_m,
    matchConfidence: row.match_confidence,
    createdAt: row.created_at,
    suggestedProjectName: row.suggested_project_name,
    suggestedProjectStatus: row.suggested_project_status,
    suggestedCustomerFirstName: row.suggested_customer_first_name,
    suggestedCustomerLastName: row.suggested_customer_last_name,
    suggestedCustomerName: row.suggested_customer_name,
    suggestedSiteAddress: row.suggested_site_address,
    suggestedSiteCity: row.suggested_site_city,
    uploadedByName: row.uploaded_by_name,
  };
}